import { MonitorUp, MonitorStop } from "lucide-react";
import { cn } from "@vc/lib/utils";
import { ControlButton } from "../common/ControlButton";
import { useTranslation } from "@/clientWeb/i18n/LanguageContext";

interface ScreenShareControlProps {
  screenOn: boolean;
  onToggleScreen: () => void;
  // Kept for backwards-compat with ControlBar; no longer used (the native picker
  // already lets the user choose Tab / Window / Entire Screen).
  onStartScreenShare?: (type: 'tab' | 'window' | 'screen') => void;
  activeShareType?: 'tab' | 'window' | 'screen' | null;
  compact?: boolean;
  /**
   * The host has turned screen sharing off. Rendered as a blocked button rather
   * than hidden, so the click can be answered with a reason — a control that
   * silently vanishes leaves the user wondering whether it ever existed.
   *
   * Note this is NOT the presenter capability: an attendee without a presenter
   * grant still gets no button at all, since that is about who may present
   * rather than a switch someone flipped.
   */
  blocked?: boolean;
  /** Called instead of toggling when [blocked]. */
  onBlocked?: () => void;
  /** Tooltip while blocked, e.g. a request to present is waiting for the host. */
  blockedLabel?: string;
}

/**
 * Single toggle button that opens the browser's native screen picker directly.
 *
 * The native getDisplayMedia picker already presents Entire Screen / Window /
 * Chrome Tab, so we don't need a custom dropdown. Crucially, this component must
 * NOT call getDisplayMedia itself — `useScreenShare` performs the single capture.
 * (The old dropdown called getDisplayMedia here AND triggered the toggle, which
 * opened the picker twice.)
 */
export function ScreenShareControl({
  screenOn,
  onToggleScreen,
  compact = false,
  blocked = false,
  onBlocked,
  blockedLabel,
}: ScreenShareControlProps) {
  const t = useTranslation();

  // Blocked wins over `screenOn`: the host closing the gate stops the share,
  // so the stop button would be answering for a share that is already ending.
  if (blocked) {
    return (
      <ControlButton
        active={false}
        onClick={onBlocked ?? (() => {})}
        label={blockedLabel ?? t("controlBar.shareScreen")}
        compact={compact}
        className="opacity-50 cursor-not-allowed"
      >
        <MonitorUp className={cn("text-white", compact ? "h-4 w-4" : "h-5 w-5")} />
      </ControlButton>
    );
  }

  if (screenOn) {
    return (
      <ControlButton
        active={true}
        onClick={onToggleScreen}
        label={t("controlBar.stopScreenSharing")}
        compact={compact}
      >
        <MonitorStop className={cn("text-white", compact ? "h-4 w-4" : "h-5 w-5")} />
      </ControlButton>
    );
  }

  return (
    <ControlButton
      active={false}
      onClick={onToggleScreen}
      label={t("controlBar.shareScreen")}
      compact={compact}
    >
      <MonitorUp className={cn("text-white", compact ? "h-4 w-4" : "h-5 w-5")} />
    </ControlButton>
  );
}
